import {createAsyncThunk} from '@reduxjs/toolkit'
import axios from 'axios'
import {
  zadProductsUrl,
  zadStockAdjustmentUrl,
  zadStockInitialUrl,
  zadStockMovementByIdUrl,
  zadStockMovementsUrl,
  zadStockProductUrl,
  zadStockUrl,
} from '../../api'
import {buildZadListQuery} from '../zad/query'
import {parseZadApiError} from '../zad/errors'
import {toZadAdjustmentPayload, toZadInitialStockPayload} from '../zad/stockHelpers'
import {
  CreateZadInitialStockRequest,
  CreateZadStockAdjustmentRequest,
  FetchZadStockByProductRequest,
  FetchZadStockMovementByIdRequest,
  FetchZadStockMovementsRequest,
  FetchZadStockRequest,
} from './types'

export const fetchZadStock = createAsyncThunk(
  'zadStock/fetchOverview',
  async (payload: FetchZadStockRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(`${zadStockUrl}?${buildZadListQuery(payload)}`)
      const {items, total, totalPages} = res.data || {}
      return {
        success: true as const,
        items: Array.isArray(items) ? items : [],
        total: typeof total === 'number' ? total : 0,
        totalPages: typeof totalPages === 'number' ? totalPages : 0,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadStockProductOptions = createAsyncThunk(
  'zadStock/fetchProductOptions',
  async (_payload: void, {rejectWithValue}) => {
    try {
      const res = await axios.get(
        `${zadProductsUrl}?${buildZadListQuery({
          pageNumber: 1,
          pageCount: 100,
          sortField: 'name',
          sortOrder: 'asc',
          isActive: 'true',
        })}`
      )
      const {products} = res.data || {}
      return {
        success: true as const,
        products: Array.isArray(products) ? products : [],
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadProductStock = createAsyncThunk(
  'zadStock/fetchProductStock',
  async (payload: FetchZadStockByProductRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(zadStockProductUrl(payload.productId))
      return {
        success: true as const,
        product: res.data.product,
        currentStock: typeof res.data.currentStock === 'number' ? res.data.currentStock : 0,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadStockMovements = createAsyncThunk(
  'zadStock/fetchMovements',
  async (payload: FetchZadStockMovementsRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(`${zadStockMovementsUrl}?${buildZadListQuery(payload)}`)
      const {movements, total, totalPages} = res.data || {}
      return {
        success: true as const,
        movements: Array.isArray(movements) ? movements : [],
        total: typeof total === 'number' ? total : 0,
        totalPages: typeof totalPages === 'number' ? totalPages : 0,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadProductRecentMovements = createAsyncThunk(
  'zadStock/fetchProductRecentMovements',
  async (payload: {productId: string}, {rejectWithValue}) => {
    try {
      const res = await axios.get(
        `${zadStockMovementsUrl}?${buildZadListQuery({
          product: payload.productId,
          pageNumber: 1,
          pageCount: 10,
          sortField: 'createdAt',
          sortOrder: 'desc',
        })}`
      )
      const {movements} = res.data || {}
      return {
        success: true as const,
        movements: Array.isArray(movements) ? movements : [],
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadStockMovementById = createAsyncThunk(
  'zadStock/fetchMovementById',
  async (payload: FetchZadStockMovementByIdRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(zadStockMovementByIdUrl(payload.id))
      return {
        success: true as const,
        movement: res.data.movement,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const createZadInitialStock = createAsyncThunk(
  'zadStock/createInitial',
  async (payload: CreateZadInitialStockRequest, {rejectWithValue}) => {
    try {
      const body = toZadInitialStockPayload(payload)
      const res = await axios.post(zadStockInitialUrl, body)
      return {
        success: true as const,
        movement: res.data.movement,
        currentStock: typeof res.data.currentStock === 'number' ? res.data.currentStock : 0,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const createZadStockAdjustment = createAsyncThunk(
  'zadStock/createAdjustment',
  async (payload: CreateZadStockAdjustmentRequest, {rejectWithValue}) => {
    try {
      const body = toZadAdjustmentPayload(payload)
      const res = await axios.post(zadStockAdjustmentUrl, body)
      return {
        success: true as const,
        movement: res.data.movement,
        currentStock: typeof res.data.currentStock === 'number' ? res.data.currentStock : 0,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)
